import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  AlertCircle,
  Baby,
  CheckCircle2,
  Circle,
  Eye,
  EyeOff,
  HeartHandshake,
  Info,
  Landmark,
  ListChecks,
  Lock,
  Pencil,
  Plus,
  ScrollText,
  Shield,
  Trash2,
  Users,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Stagger, StaggerItem } from "@/components/FadeIn";
import { M3Card, M3CardHeader } from "@/components/M3Shared";
import { M3BottomSheet } from "@/components/M3BottomSheet";
import { MemberAvatar } from "@/components";
import { registerFab } from "@/components/FabRegistry";
import { usePublishModuleSlot } from "@/lib/ModuleContext";
import { cn } from "@/lib/utils";
import { useCurrentUser } from "@/lib/useCurrentUser";
import { useCurrentFamily } from "@/lib/useCurrentFamily";
import { useSupabaseQuery, useRealtimeTable } from "@/lib/dbHooks";
import { supabase, isAuthConfigured } from "@/lib/supabase";
import type { Database } from "@/types/db";

type TodoItemRow = Database["public"]["Tables"]["todo_items"]["Row"];
type Todo = {
  id: string;
  text: string;
  assigneeId: string | null;     // null = "anyone"
  assigneeLabel: string;
  assigneeInitials: string;
  assigneeColor: string;
  /** Global avatar (profiles.avatar_url) when the assignee is the
   *  signed-in user; null otherwise. */
  assigneeAvatarUrl?: string | null;
  done: boolean;
  createdByMemberId: string | null;
  isOwner: boolean;              // mirrors RLS `todo_items_delete`:
                                 //   created_by_member_id = caller
};

const ANYONE_COLOR = "from-zinc-300 to-zinc-500";
const ANYONE_INITIALS = "任";
const ANYONE_LABEL = "任何人";

// Legacy note kinds. The `kind` column on `family_legacy_notes` is a
// closed enum (migration 05001) — this catalog mirrors it exactly. The
// UI maps each kind to an icon and a Traditional-Chinese label so the
// user picks by visual rather than reading raw keys. Adding a new kind
// means: add to the migration CHECK, add here, ship.
type LegacyKind = "estate" | "will" | "guardianship" | "funeral" | "medical" | "other";
type LegacyNoteRow = Database["public"]["Tables"]["family_legacy_notes"]["Row"];

const LEGACY_KINDS: ReadonlyArray<{
  key: LegacyKind;
  label: string;
  hint: string;
  Icon: LucideIcon;
}> = [
  { key: "will",         label: "遺囑",         hint: "我嘅遺產同意願分配",    Icon: ScrollText },
  { key: "estate",       label: "遺產",         hint: "資產、戶口、保險箱等資料", Icon: Landmark },
  { key: "guardianship", label: "監護人",       hint: "未成年子女嘅照顧安排",    Icon: Baby },
  { key: "medical",      label: "醫療指示",     hint: "急救、維生治療意願",      Icon: HeartHandshake },
  { key: "funeral",      label: "殯葬意願",     hint: "儀式、墓地、訃聞安排",    Icon: Shield },
  { key: "other",        label: "其他",         hint: "其他想留低嘅事",          Icon: Info },
];

const LEGACY_LABEL: Record<LegacyKind, string> = Object.fromEntries(
  LEGACY_KINDS.map((k) => [k.key, k.label]),
) as Record<LegacyKind, string>;

export function FamListView() {
  const me0 = useCurrentUser();
  const cf  = useCurrentFamily(me0.user?.id ?? null);

  const [showAdd, setShowAdd] = useState(false);
  const [draftText, setDraftText] = useState("");
  const [draftAssignee, setDraftAssignee] = useState<string | "anyone">(cf.self?.id ?? "anyone");
  const [actionError, setActionError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const items = useSupabaseQuery<TodoItemRow[]>(async () => {
    if (!isAuthConfigured() || !cf.list?.id) return { data: [], error: null };
    return await supabase
      .from("todo_items")
      .select("*")
      .eq("list_id", cf.list.id)
      .order("created_at", { ascending: true });
  }, [cf.list?.id]);

  useRealtimeTable<"todo_items">("todo_items", cf.list?.id ? `list_id=eq.${cf.list.id}` : null,
    () => items.refresh());

  // ───────────────────────  Legacy notes (傳承筆記)  ─────────────────
  // The user wanted a place inside FamList to write 遺產 / 遺囑 / 監護人
  // / 殯葬意願 / 醫療指示 etc. We model each as one row per (member,
  // kind) on `family_legacy_notes` (migration 05001). The writer is
  // always `cf.self` — RLS makes that the only author the API will
  // accept. Notes are PRIVATE by default; the user can opt in to sharing
  // the body with the rest of the family via the sheet's toggle.
  const [showLegacy, setShowLegacy] = useState(false);
  const [legacyKind, setLegacyKind] = useState<LegacyKind>("will");
  const [legacyEditing, setLegacyEditing] = useState(false);
  const [legacyTitle, setLegacyTitle] = useState("");
  const [legacyBody, setLegacyBody] = useState("");
  const [legacyShare, setLegacyShare] = useState(false);
  const legacyTextareaRef = useRef<HTMLTextAreaElement>(null);

  const legacy = useSupabaseQuery<LegacyNoteRow[]>(async () => {
    if (!isAuthConfigured() || !cf.family?.id) return { data: [], error: null };
    return await supabase
      .from("family_legacy_notes")
      .select("*")
      .eq("family_id", cf.family.id)
      .order("updated_at", { ascending: false });
  }, [cf.family?.id]);

  useRealtimeTable<"family_legacy_notes">(
    "family_legacy_notes",
    cf.family?.id ? `family_id=eq.${cf.family.id}` : null,
    () => legacy.refresh(),
  );

  const ownLegacyByKind: Partial<Record<LegacyKind, LegacyNoteRow>> = useMemo(() => {
    const selfMemberId = cf.self?.id;
    const map: Partial<Record<LegacyKind, LegacyNoteRow>> = {};
    for (const row of (legacy.data ?? [])) {
      if (selfMemberId && row.author_member_id === selfMemberId) {
        if (map[row.kind as LegacyKind] == null) {
          map[row.kind as LegacyKind] = row;
        }
      }
    }
    return map;
  }, [legacy.data, cf.self?.id]);

  const closeLegacySheet = () => {
    setShowLegacy(false);
    setLegacyTitle("");
    setLegacyBody("");
    setLegacyShare(false);
    setLegacyEditing(false);
  };

  // Opening a tile: ALWAYS read-only first. A kind that already has
  // a note shows its content; an empty kind shows a "nothing here
  // yet" placeholder. The pencil (新增/編輯) enters edit mode.
  const openLegacySheet = (kind: LegacyKind) => {
    if (!cf.self?.id) {
      setActionError("家庭資料未準備好，請稍後再試。");
      return;
    }
    const existing = ownLegacyByKind[kind] ?? null;
    setLegacyKind(kind);
    setLegacyTitle(existing?.title ?? "");
    setLegacyBody(existing?.body ?? "");
    setLegacyShare(!!existing?.is_shared_with_family);
    setLegacyEditing(false);
    setShowLegacy(true);
  };

  // Switching kind inside the open sheet stays read-only too, but the
  // displayed draft must be hydrated from the newly selected note. Without
  // resetting these fields, the sheet keeps showing the previous kind's
  // title/body/share state even though the heading and empty/view mode
  // changed.
  const selectLegacyKind = (kind: LegacyKind) => {
    const existing = ownLegacyByKind[kind] ?? null;
    setLegacyKind(kind);
    setLegacyTitle(existing?.title ?? "");
    setLegacyBody(existing?.body ?? "");
    setLegacyShare(!!existing?.is_shared_with_family);
    setLegacyEditing(false);
    setActionError(null);
  };

  const saveLegacy = async () => {
    const body = legacyBody; // empty body is allowed — a stub "no assets" line is still meaningful
    const title = legacyTitle.trim() || null;
    if (!cf.family?.id || !cf.self?.id) {
      setActionError("家庭資料未準備好，請稍後再試。");
      return;
    }
    setActionError(null);
    const existing = ownLegacyByKind[legacyKind] ?? null;
    if (existing) {
      const upd = await supabase
        .from("family_legacy_notes")
        .update({ body, title, is_shared_with_family: legacyShare })
        .eq("id", existing.id);
      if (upd.error) {
        setActionError(`儲存失敗：${upd.error.message}`);
        return;
      }
    } else {
      const ins = await supabase
        .from("family_legacy_notes")
        .insert({
          family_id: cf.family.id,
          author_member_id: cf.self.id,
          kind: legacyKind,
          title,
          body,
          is_shared_with_family: legacyShare,
        });
      if (ins.error) {
        // 23505 = unique (author_member_id, kind) already taken —
        // the tile list was stale (e.g. a transient read failure
        // hid the existing note). Self-heal by updating the row
        // the backend says exists instead of failing the add.
        if (ins.error.code === "23505") {
          const { data: existingRows } = await supabase
            .from("family_legacy_notes")
            .select("id")
            .eq("family_id", cf.family.id)
            .eq("author_member_id", cf.self.id)
            .eq("kind", legacyKind)
            .maybeSingle();
          if (existingRows?.id) {
            const upd = await supabase
              .from("family_legacy_notes")
              .update({ body, title, is_shared_with_family: legacyShare })
              .eq("id", existingRows.id);
            if (upd.error) {
              setActionError(`儲存失敗：${upd.error.message}`);
              return;
            }
          } else {
            setActionError(`新增失敗：${ins.error.message}`);
            return;
          }
        } else {
          setActionError(`新增失敗：${ins.error.message}`);
          return;
        }
      }
    }
    await legacy.refresh();
    closeLegacySheet();
  };

  const deleteLegacy = async (row: LegacyNoteRow) => {
    if (typeof window !== "undefined"
        && !window.confirm(`確定刪除「${LEGACY_LABEL[(row.kind as LegacyKind)] ?? row.kind}」呢篇筆記？呢個動作無法復原。`)) {
      return;
    }
    setActionError(null);
    const del = await supabase.from("family_legacy_notes").delete().eq("id", row.id);
    if (del.error) {
      setActionError(`刪除失敗：${del.error.message}`);
      return;
    }
    await legacy.refresh();
    // The sheet was open on a note that no longer exists — close it
    // (saveLegacy does the same after a successful write).
    closeLegacySheet();
  };

  useEffect(() => {
    if (showLegacy) {
      // Defer to next frame so the textarea is mounted before we focus;
      // focusing on the open transition flickers on iOS Safari.
      const t = window.setTimeout(() => legacyTextareaRef.current?.focus(), 80);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [showLegacy, legacyKind]);


  useEffect(() => {
    registerFab({
      label: "新增事項",
      onClick: () => setShowAdd(true),
      icon: Plus,
    });
    // Bug fix (F6): `FabRegistry` is a module-level singleton. The
    // previous code only registered on mount without ever
    // unregistering, so leaving this view (or a Strict Mode
    // double-mount in dev) left the FAB pointing at `setShowAdd`
    // of an unmounted component, or showed the FAB on screens
    // that have no FAB of their own. Clean up on unmount.
    return () => { registerFab(null); };
  }, []);

  useEffect(() => {
    if (showAdd) inputRef.current?.focus();
  }, [showAdd]);

  const todos: Todo[] = useMemo(() => {
    const selfMemberId = cf.self?.id ?? null;
    return (items.data ?? []).map(it => {
      const a = it.assignee_member_id
        ? cf.members.find(m => m.id === it.assignee_member_id)
        : null;
      return {
        id: it.id,
        text: it.text,
        assigneeId: it.assignee_member_id,
        assigneeLabel: a?.display_name ?? ANYONE_LABEL,
        assigneeInitials: a?.initials ?? ANYONE_INITIALS,
        assigneeColor: a?.avatar_color ?? ANYONE_COLOR,
        done: it.done,
        createdByMemberId: it.created_by_member_id ?? null,
        // Global avatar flows only when the assignee IS the
        // signed-in user's own family_member row.
        assigneeAvatarUrl: a && a.id === cf.self?.id ? (me0.avatarUrl ?? null) : null,
        // The delete RLS policy (todo_items_delete, migration 014)
        // is keyed to `created_by_member_id = auth.uid()`'s member
        // id, so `isOwner` should match the SAME condition — not
        // "can I see it" (family_members can read), but "did I
        // create it" (only the author can remove).
        isOwner: !!selfMemberId && !!it.created_by_member_id
          && it.created_by_member_id === selfMemberId,
      };
    });
  }, [items.data, cf.members, cf.self?.id, me0.avatarUrl]);

  const completedCount = todos.filter(t => t.done).length;
  const openCount = todos.length - completedCount;

  const toggleDone = async (id: string) => {
    // Bug fix (F5): look up the current row state from the latest
    // `items.data` snapshot, not the `todos` memo (which can race
    // with realtime refresh). Bug fix (F2): set `done_at` on
    // completion so the column matches the API spec and downstream
    // "next up" sorting (`family_upcoming` orders by `due_at`)
    // behaves consistently. Bug fix (F4): surface errors via
    // `actionError`.
    const row = (items.data ?? []).find((r) => r.id === id);
    if (!row) return;
    const nextDone = !row.done;
    const patch: { done: boolean; done_at: string | null } = {
      done: nextDone,
      done_at: nextDone ? new Date().toISOString() : null,
    };
    setActionError(null);
    const upd = await supabase.from("todo_items").update(patch).eq("id", id);
    if (upd.error) {
      setActionError(`更新失敗：${upd.error.message}`);
      return;
    }
    await items.refresh();
  };

  // Delete a todo item. Only the row's creator can delete — the
  // RLS policy `todo_items_delete` (migration 014) keys the
  // permission to `created_by_member_id = auth.uid()`. We guard
  // here too so the trash button is only shown to creators (see
  // the row JSX below), but the server is still the source of
  // truth — if someone POSTs through the REST API with someone
  // else's id, RLS rejects the call and we surface the error
  // through `actionError`. We prompt via window.confirm so a
  // stray tap on the trash icon doesn't nuke a shared list item.
  const deleteTodo = async (todo: Todo) => {
    if (!todo.isOwner) {
      setActionError("只有建立者可以刪除呢個項目。");
      return;
    }
    if (typeof window !== "undefined"
        && !window.confirm(`確定刪除「${todo.text}」？呢個動作無法復原。`)) {
      return;
    }
    setActionError(null);
    const del = await supabase.from("todo_items").delete().eq("id", todo.id);
    if (del.error) {
      setActionError(`刪除失敗：${del.error.message}`);
      return;
    }
    await items.refresh();
  };

  const closeAdd = () => {
    setShowAdd(false);
    setDraftText("");
    setDraftAssignee(cf.self?.id ?? "anyone");
  };

  const addTodo = async () => {
    const text = draftText.trim();
    if (!text) return;
    // Bug fix (F1): the previous code sent `created_by_member_id:
    // cf.self?.id ?? null`, which fires the insert with null when
    // `cf.self` was still loading. The RLS policy
    // (todo_items_insert) requires `created_by_member_id` to be
    // the caller's member id — null rows get rejected silently
    // because the insert error was never surfaced. Guard first
    // and surface any error via the new `actionError` state.
    if (!cf.list?.id || !cf.self?.id) {
      setActionError("家庭資料未準備好，請稍後再試。");
      return;
    }
    setActionError(null);
    const insert = await supabase.from("todo_items").insert({
      list_id: cf.list.id,
      text,
      assignee_member_id: draftAssignee === "anyone" ? null : draftAssignee,
      created_by_member_id: cf.self.id,
    });
    if (insert.error) {
      setActionError(`新增失敗：${insert.error.message}`);
      return;
    }
    await items.refresh();
    closeAdd();
  };

  // Right-rail context for `xl+` (wide desktop). Summarises the
  // list at a glance: progress bar, per-assignee breakdown, and
  // the user's open items. Re-built only when the underlying data
  // shifts, not on every render.
  const groupedByAssignee = useMemo(() => {
    const byKey = new Map<string, Todo[]>();
    for (const t of todos) {
      const key = t.assigneeId ?? "anyone";
      if (!byKey.has(key)) byKey.set(key, []);
      byKey.get(key)!.push(t);
    }
    return Array.from(byKey.entries()).map(([key, list]) => {
      const sample = list[0];
      if (!sample) return null;
      return {
        key,
        label: sample.assigneeLabel,
        initials: sample.assigneeInitials,
        color: sample.assigneeColor,
        avatarUrl: sample.assigneeAvatarUrl ?? null,
        total: list.length,
        done: list.filter((t) => t.done).length,
      };
    }).filter((x): x is NonNullable<typeof x> => x !== null);
  }, [todos]);
  const myOpen = useMemo(
    () => todos.filter((t) => !t.done && (t.assigneeId === cf.self?.id || t.assigneeId === null)),
    [todos, cf.self?.id],
  );
  // Memoize the right-rail slot. `usePublishModuleSlot` short-
  // circuits when the slot object identity is unchanged; passing
  // an inline object (the previous pattern) creates a fresh
  // reference on every render, which the hook's effect treated
  // as "changed" and called Provider.setSlot, feeding a
  // `setSlot → Provider re-render → ...` cycle visible to React
  // as "Maximum update depth exceeded" (prod 2026-08-04). Today-
  // View already used useMemo here; we match that pattern.
  usePublishModuleSlot(useMemo(() => ({
    pageTitle: cf.list?.name ?? "FamList",
    pageSubtitle: cf.family?.name ?? undefined,
    context: (
      <div className="flex flex-col gap-3">
        <M3Card className="border-border/40">
          <M3CardHeader
            title="進度"
            icon={<ListChecks className="h-4 w-4" aria-hidden />}
            trailing={`${completedCount} / ${todos.length}`}
          />
          <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-secondary">
            <div
              className="h-full rounded-full bg-primary transition-[width] duration-300"
              style={{ width: `${todos.length ? Math.round((completedCount / todos.length) * 100) : 0}%` }}
              aria-hidden
            />
          </div>
          <div className="mt-2 flex items-center justify-between text-[11px] text-muted-foreground">
            <span>未完成 {openCount}</span>
            <span>已完成 {completedCount}</span>
          </div>
        </M3Card>
        <M3Card className="border-border/40">
          <M3CardHeader
            title="按負責人"
            icon={<Users className="h-4 w-4" aria-hidden />}
          />
          <ul className="mt-3 space-y-1.5">
            {groupedByAssignee.map((g) => (
              <li key={g.key} className="flex items-center gap-2.5 rounded-xl px-2 py-1.5 text-sm">
                <MemberAvatar
                  initials={g.initials}
                  color={g.color}
                  avatarUrl={g.avatarUrl ?? null}
                  size="sm"
                />
                <div className="min-w-0 flex-1 truncate text-foreground/85">{g.label}</div>
                <div className="shrink-0 text-[11px] tabular-nums text-muted-foreground">
                  {g.done} / {g.total}
                </div>
              </li>
            ))}
          </ul>
        </M3Card>
        {myOpen.length > 0 && (
          <M3Card className="border-border/40">
            <M3CardHeader title="你嘅項目" />
            <ul className="mt-3 space-y-1.5">
              {myOpen.slice(0, 5).map((t) => (
                <li key={t.id} className="rounded-xl bg-secondary/40 px-2.5 py-1.5 text-sm">
                  <div className="truncate">{t.text}</div>
                </li>
              ))}
            </ul>
          </M3Card>
        )}
      </div>
    ),
  }), [
    cf.list?.name,
    cf.family?.name,
    completedCount,
    todos.length,
    openCount,
    groupedByAssignee,
    myOpen,
  ]));

  return (
    <div className="space-y-4 sm:space-y-6">
      <motion.header
        className="px-1 pt-1"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
      >
        <p className="text-xs text-muted-foreground sm:text-sm">
          FamList · 家庭事項
        </p>
        <h1 className="mt-1 font-serif text-2xl font-semibold tracking-tight sm:text-3xl lg:text-4xl">
          {openCount === 0 ? "全部完成 🎉" : `${openCount} 件待辦`}
        </h1>
        <p className="mt-2 text-pretty text-sm text-muted-foreground sm:text-base">
          共同分擔照顧責任。{cf.list?.name ?? ""}
        </p>
      </motion.header>

      <M3Card>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ListChecks className="h-4 w-4 text-primary" aria-hidden />
            <span className="text-sm font-semibold">
              {completedCount} / {todos.length} 已完成
            </span>
          </div>
          <span className="text-[11px] text-muted-foreground">
            <Users className="mr-1 inline h-3 w-3" aria-hidden />
            {cf.members.length} 位成員
          </span>
        </div>
      </M3Card>
      {/* Page-top error banner. Suppressed while a bottom sheet is
       * open — the sheets render their own in-sheet alert so the
       * error is visible above the backdrop instead of hidden
       * behind the modal. */}
      {(items.error || legacy.error || cf.error || actionError) && !showAdd && !showLegacy && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-2xl border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive"
        >
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <div className="min-w-0 flex-1">
            {items.error ?? legacy.error ?? cf.error ?? actionError}
          </div>
        </div>
      )}

      {todos.length === 0 && !items.loading && (
        <div className="rounded-2xl border border-dashed border-border/60 p-8 text-center text-sm text-muted-foreground">
          仲未有任何事項。撳右下角「＋」新增。
        </div>
      )}

      <Stagger as="ul" className="space-y-2" gap={0.05}>
        <AnimatePresence initial={false}>
          {todos.map(todo => {
            const member = todo.assigneeId
              ? cf.members.find(m => m.id === todo.assigneeId)
              : null;
            return (
              <StaggerItem
                key={todo.id}
                as="li"
                className={cn(
                  "flex items-center gap-3 rounded-2xl border border-border/40 bg-card p-3 transition-opacity",
                  todo.done && "opacity-50",
                )}
              >
                <button
                  type="button"
                  aria-label={todo.done ? "標記為未完成" : "標記為完成"}
                  onClick={() => toggleDone(todo.id)}
                  className="shrink-0"
                >
                  {todo.done
                    ? <CheckCircle2 className="h-6 w-6 text-primary" aria-hidden />
                    : <Circle className="h-6 w-6 text-muted-foreground" aria-hidden />}
                </button>
                <MemberAvatar
                  initials={todo.assigneeInitials}
                  color={todo.assigneeColor}
                  avatarUrl={todo.assigneeAvatarUrl ?? null}
                  size="sm"
                />
                <div className="min-w-0 flex-1">
                  <div className={cn("text-sm", todo.done && "line-through text-muted-foreground")}>
                    {todo.text}
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    {todo.assigneeLabel}
                  </div>
                </div>
                {/*
                  Trash button: only renders for the creator. The delete
                  RLS policy would reject a non-creator's attempt anyway
                  (todo_items_delete, migration 014), but rendering only
                  when `isOwner` gives users the right affordance — they
                  can never see a "delete" button they're not allowed to
                  click. Hidden on completed rows would also be
                  reasonable; here we keep it visible regardless of done
                  state so a user can clean up finished items in one tap.
                */}
                {todo.isOwner && (
                  <button
                    type="button"
                    aria-label={`刪除項目「${todo.text}」`}
                    onClick={() => void deleteTodo(todo)}
                    className="ml-1 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive active:bg-destructive/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-destructive/40"
                  >
                    <Trash2 className="h-4 w-4" aria-hidden />
                  </button>
                )}
              </StaggerItem>
            );
          })}
        </AnimatePresence>
      </Stagger>

      {/* ───────────────────────  Legacy notes (傳承筆記)  ─────────────── */}
      <section
        aria-labelledby="legacy-notes-heading"
        className="space-y-3 rounded-2xl border border-dashed border-border/60 bg-card/40 p-3 sm:p-4"
      >
        <header className="flex items-start gap-3">
          <span
            aria-hidden
            className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary"
          >
            <ScrollText className="h-5 w-5" aria-hidden />
          </span>
          <div className="min-w-0 flex-1">
            <h2
              id="legacy-notes-heading"
              className="font-serif text-base font-semibold leading-tight sm:text-lg"
            >
              傳承筆記
            </h2>
            <p className="mt-0.5 text-[11px] leading-relaxed text-muted-foreground sm:text-xs">
              寫低你嘅遺產、遺囑、監護人、殯葬意願、醫療指示⋯⋯
              <span className="ml-1 font-medium text-foreground/80">預設只有你自己睇到。</span>
              想同家人分享，再自行開啟。
            </p>
          </div>
        </header>

        <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {LEGACY_KINDS.map((k) => {
            const existing = ownLegacyByKind[k.key] ?? null;
            const Icon = k.Icon;
            const filled = !!existing;
            const shared = filled && existing!.is_shared_with_family;
            return (
              <li key={k.key}>
                <button
                  type="button"
                  onClick={() => openLegacySheet(k.key)}
                  aria-label={`${k.label} — ${filled ? "已填寫" : "未填寫"}`}
                  className={cn(
                    "group flex w-full items-center gap-2 rounded-2xl border bg-card p-2.5 text-left transition-colors active:scale-[0.99]",
                    filled
                      ? "border-primary/40 bg-primary/5 hover:bg-primary/10"
                      : "border-border/60 hover:border-border hover:bg-secondary/40",
                  )}
                >
                  <span
                    aria-hidden
                    className={cn(
                      "grid h-9 w-9 shrink-0 place-items-center rounded-xl",
                      filled
                        ? "bg-primary text-primary-foreground"
                        : "bg-secondary text-foreground/70",
                    )}
                  >
                    <Icon className="h-4 w-4" aria-hidden />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">{k.label}</div>
                    <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
                      {filled ? (
                        <>
                          {shared ? (
                            <>
                              <Eye className="h-3 w-3" aria-hidden />
                              <span>已同家人分享</span>
                            </>
                          ) : (
                            <>
                              <EyeOff className="h-3 w-3" aria-hidden />
                              <span>只限你本人</span>
                            </>
                          )}
                        </>
                      ) : (
                        <span className="line-clamp-1">{k.hint}</span>
                      )}
                    </div>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>

        <p className="px-1 pt-1 text-[10px] leading-relaxed text-muted-foreground sm:text-[11px]">
          <Lock className="mr-1 inline h-3 w-3" aria-hidden />
          每項只限一篇。寫下後預設只有你（作者）讀得到；分享前 RLS 會攔截。
        </p>
      </section>

      {/* Add sheet */}
      <AnimatePresence>
        {showAdd && (
          <M3BottomSheet
            onClose={closeAdd}
            title="新增事項"
          >
            <div className="space-y-3">
              <input
                ref={inputRef}
                value={draftText}
                onChange={(e) => setDraftText(e.currentTarget.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void addTodo(); }
                }}
                placeholder="例如：帶嫲嫲去覆診"
                className="h-11 w-full rounded-full border border-border/60 bg-background px-4 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
              />
              <div>
                <div className="text-[11px] font-medium text-muted-foreground">交畀</div>
                <div className="mt-2 grid grid-cols-3 gap-2">
                  <button
                    type="button"
                    onClick={() => setDraftAssignee("anyone")}
                    className={cn(
                      "flex flex-col items-center rounded-2xl border px-2 py-2 text-xs transition-colors",
                      draftAssignee === "anyone"
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-border/60 bg-card text-foreground/80",
                    )}
                  >
                    <div className="grid h-7 w-7 mx-auto place-items-center rounded-full bg-gradient-to-br from-zinc-300 to-zinc-500 text-[11px] font-semibold text-white">任</div>
                    <div className="mt-1">任何人</div>
                  </button>
                  {cf.members.map(m => (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => setDraftAssignee(m.id)}
                      className={cn(
                        "flex flex-col items-center rounded-2xl border px-2 py-2 text-xs transition-colors",
                        draftAssignee === m.id
                          ? "border-primary bg-primary/10 text-primary"
                          : "border-border/60 bg-card text-foreground/80",
                      )}
                    >
                      <MemberAvatar
                        initials={m.initials}
                        color={m.avatar_color}
                        avatarUrl={m.id === cf.self?.id ? (me0.avatarUrl ?? null) : null}
                        size="sm"
                      />
                      <div className="mt-1 truncate">{m.display_name}</div>
                    </button>
                  ))}
                </div>
              </div>
              <button
                type="button"
                onClick={() => void addTodo()}
                disabled={!draftText.trim()}
                className="h-11 w-full rounded-full bg-primary text-sm font-medium text-primary-foreground disabled:opacity-50"
              >
                新增
              </button>
              {actionError && (
                <div
                  role="alert"
                  className="flex items-start gap-2 rounded-2xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800"
                >
                  <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                  <span className="min-w-0">{actionError}</span>
                </div>
              )}
            </div>
          </M3BottomSheet>
        )}
      </AnimatePresence>

      {/* Legacy note edit sheet — opened from any tile inside the
          傳承筆記 section. Sharing is OFF by default (privacy by
          design); the toggle is the only way to expose the body to
          other family members. RLS enforces the same boundary server
          side, so a stray direct REST call still can't bypass it. */}
      <AnimatePresence>
        {showLegacy && (
          <M3BottomSheet
            onClose={closeLegacySheet}
            title={`${LEGACY_LABEL[legacyKind]}筆記`}
            className="max-h-[92vh]"
          >
            <div className="space-y-4">
              <div>
                <div className="text-[11px] font-medium text-muted-foreground">類型</div>
                <div className="mt-2 grid grid-cols-3 gap-2">
                  {LEGACY_KINDS.map((k) => {
                    const Icon = k.Icon;
                    const active = legacyKind === k.key;
                    return (
                      <button
                        key={k.key}
                        type="button"
                        onClick={() => selectLegacyKind(k.key)}
                        aria-pressed={active}
                        className={cn(
                          "rounded-2xl border px-2 py-2 text-xs transition-colors",
                          active
                            ? "border-primary bg-primary/10 text-primary"
                            : "border-border/60 bg-card text-foreground/80 hover:border-border",
                        )}
                      >
                        <Icon className="mx-auto h-4 w-4" aria-hidden />
                        <div className="mt-1 truncate">{k.label}</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Read-only view — filled notes show their content;
               * empty kinds show a "nothing here yet" placeholder.
               * Editing is always behind the pencil (新增/編輯). */}
              {!legacyEditing ? (
                <>
                  {ownLegacyByKind[legacyKind] ? (
                    <div
                      className="rounded-2xl bg-secondary/40 p-4"
                      data-testid="legacy-view"
                    >
                      {legacyTitle ? (
                        <h3 className="font-serif text-base font-semibold">
                          {legacyTitle}
                        </h3>
                      ) : null}
                      <p
                        className={cn(
                          "whitespace-pre-wrap text-sm leading-relaxed text-foreground/90",
                          legacyTitle && "mt-2",
                          !legacyBody && "text-muted-foreground",
                        )}
                      >
                        {legacyBody || "（未寫內容）"}
                      </p>
                      <div className="mt-3 flex items-center gap-1.5 text-[11px] text-muted-foreground">
                        {legacyShare ? (
                          <Eye className="h-3.5 w-3.5" aria-hidden />
                        ) : (
                          <EyeOff className="h-3.5 w-3.5" aria-hidden />
                        )}
                        {legacyShare ? "已同家人分享" : "只限你本人"}
                      </div>
                    </div>
                  ) : (
                    <div
                      className="rounded-2xl border border-dashed border-border/60 bg-card/40 p-6 text-center"
                      data-testid="legacy-empty"
                    >
                      <ScrollText className="mx-auto h-6 w-6 text-muted-foreground/50" aria-hidden />
                      <p className="mt-2 text-sm font-medium text-foreground/70">
                        呢度暫時未有內容
                      </p>
                      <p className="mt-1 text-[11px] text-muted-foreground">
                        撳「新增」寫低你嘅{LEGACY_LABEL[legacyKind]}安排。
                      </p>
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={() => setLegacyEditing(true)}
                    data-testid="legacy-edit-btn"
                    className="inline-flex h-11 w-full items-center justify-center gap-1.5 rounded-full bg-primary text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
                  >
                    <Pencil className="h-4 w-4" aria-hidden />
                    {ownLegacyByKind[legacyKind] ? "編輯" : "新增"}
                  </button>

                  {ownLegacyByKind[legacyKind] && (
                    <button
                      type="button"
                      onClick={() => void deleteLegacy(ownLegacyByKind[legacyKind]!)}
                      className="mx-auto flex items-center gap-1.5 rounded-full px-2 py-1 text-xs text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                    >
                      <Trash2 className="h-3.5 w-3.5" aria-hidden />
                      刪除呢篇筆記
                    </button>
                  )}
                </>
              ) : (
                <>
                  <div>
                    <label
                      htmlFor="legacy-title"
                      className="text-[11px] font-medium text-muted-foreground"
                    >
                      標題（選填）
                    </label>
                    <input
                      id="legacy-title"
                      value={legacyTitle}
                      onChange={(e) => setLegacyTitle(e.currentTarget.value)}
                      placeholder={`例：${LEGACY_LABEL[legacyKind]}重點`}
                      maxLength={80}
                      className="mt-1 h-11 w-full rounded-2xl border border-border/60 bg-background px-3.5 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="legacy-body"
                      className="flex items-center justify-between text-[11px] font-medium text-muted-foreground"
                    >
                      <span>內容</span>
                      <span className="tabular-nums">{legacyBody.length} / 8000</span>
                    </label>
                    <textarea
                      id="legacy-body"
                      ref={legacyTextareaRef}
                      value={legacyBody}
                      onChange={(e) =>
                        setLegacyBody(e.currentTarget.value.slice(0, 8000))
                      }
                      rows={8}
                      placeholder={`寫低你嘅${LEGACY_LABEL[legacyKind]}內容，例如資產清單、聯絡人、意願⋯⋯`}
                      className="mt-1 block w-full resize-y rounded-2xl border border-border/60 bg-background px-3.5 py-3 text-sm leading-relaxed focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
                    />
                  </div>

                  {ownLegacyByKind[legacyKind] && (
                    <button
                      type="button"
                      onClick={() => void deleteLegacy(ownLegacyByKind[legacyKind]!)}
                      className="inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-xs text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                    >
                      <Trash2 className="h-3.5 w-3.5" aria-hidden />
                      刪除呢篇筆記
                    </button>
                  )}

                  <label
                    className={cn(
                      "flex cursor-pointer items-start gap-3 rounded-2xl border p-3 transition-colors",
                      legacyShare
                        ? "border-primary/40 bg-primary/5"
                        : "border-border/60 bg-card",
                    )}
                  >
                    <span
                      aria-hidden
                      className={cn(
                        "relative mt-0.5 inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors",
                        legacyShare ? "bg-primary" : "bg-muted",
                      )}
                    >
                      <span
                        className={cn(
                          "inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform",
                          legacyShare ? "translate-x-5" : "translate-x-0.5",
                        )}
                      />
                    </span>
                    <span className="min-w-0 flex-1 text-sm">
                      <span className="flex items-center gap-1.5 font-medium text-foreground">
                        {legacyShare ? (
                          <Eye className="h-3.5 w-3.5" aria-hidden />
                        ) : (
                          <EyeOff className="h-3.5 w-3.5" aria-hidden />
                        )}
                        {legacyShare ? "已同家人分享" : "只限你本人"}
                      </span>
                      <span className="block text-[11px] leading-relaxed text-muted-foreground">
                        {legacyShare
                          ? "家人成員可以喺「傳承筆記」睇到呢篇內容。"
                          : "未開啟前，只有你（作者）透過 RLS 讀得到。RLS 喺資料庫層強制，分享狀態改變先會改變可見性。"}
                      </span>
                    </span>
                    <input
                      id="legacy-share-toggle"
                      type="checkbox"
                      className="sr-only"
                      checked={legacyShare}
                      onChange={(e) => setLegacyShare(e.currentTarget.checked)}
                    />
                  </label>

                  <button
                    type="button"
                    onClick={() => void saveLegacy()}
                    className="h-11 w-full rounded-full bg-primary text-sm font-medium text-primary-foreground disabled:opacity-50"
                  >
                    {ownLegacyByKind[legacyKind] ? "儲存" : "新增"}
                  </button>
                </>
              )}
              {actionError && (
                <div
                  role="alert"
                  data-testid="legacy-sheet-error"
                  className="flex items-start gap-2 rounded-2xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800"
                >
                  <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                  <span className="min-w-0">{actionError}</span>
                </div>
              )}
            </div>
          </M3BottomSheet>
        )}
      </AnimatePresence>

      <p className="px-2 pt-2 text-center text-[11px] leading-relaxed text-muted-foreground">
        <Lock className="mr-1 inline h-3 w-3" aria-hidden />
        私人事項只限你本人可見。家庭事項預設全家共享。
      </p>
    </div>
  );
}

export default FamListView;
